import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { IContentStateChangeResponseDto } from 'src/app/core/dtos/interfaces/i-content-state-change-response-dto';
import { ISaveGalleryDto } from 'src/app/core/dtos/interfaces/i-save-gallery-dto';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { IRepurposeContentResponse } from 'src/app/core/dtos/interfaces/i-repurpose-content-response-dto';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { ISpikeContentResponseDto } from 'src/app/core/dtos/interfaces/i-spike-content-response-dto';
import { RestService } from 'src/app/shared/services/rest-service';
import { IGalleryContentDto } from 'src/app/core/dtos/interfaces/i-gallery-content-dto';
import { map } from 'rxjs/operators';
import { GalleryContentDto } from 'src/app/core/dtos/gallery-content-dto';

@Injectable({
  providedIn: 'root',
})
export class GalleryContentTypeService {
  static readonly ENTITY_IDENTIFIER: string = 'galleries';
  private readonly BASE_CONTENT_ROUTE = `api/${GalleryContentTypeService.ENTITY_IDENTIFIER}`;
  private readonly BUILD_HTTP_ROUTE = (...args) =>
    this.restService.BUILD_HTTP_ROUTE(this.BASE_CONTENT_ROUTE, ...args);

  constructor(private restService: RestService) {}

  create$(request: ISaveGalleryDto): Observable<ISaveContentResponseDto> {
    return this.restService.post$<ISaveContentResponseDto>(
      this.BUILD_HTTP_ROUTE(),
      request
    );
  }

  createVersion$(
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> {
    return this.restService.post$<INewContentVersionResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      null
    );
  }

  getById$(
    contentId: string,
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<IGalleryContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<IGalleryContentDto>(this.BUILD_HTTP_ROUTE(`${contentId}`), {
        params,
      })
      .pipe(map((x) => new GalleryContentDto(x)));
  }

  getByVersionId$(
    versionId?: string,
    returnEvenDeleted: boolean = false
  ): Observable<IGalleryContentDto> {
    const params = this.restService.buildHttpParamsObject({
      versionId: versionId,
      returnEvenDeleted: returnEvenDeleted,
    });
    return this.restService
      .get$<IGalleryContentDto>(this.BUILD_HTTP_ROUTE(`-`), { params })
      .pipe(map((x) => new GalleryContentDto(x)));
  }

  update$(
    contentId: string,
    contentVersion: IGalleryContentDto
  ): Observable<IGalleryContentDto> {
    let result = this.restService.put$<IGalleryContentDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}`),
      contentVersion
    );
    return result;
  }

  delete$(
    contentId: string,
    overrideDeleteDate: Date = null
  ): Observable<Object> {
    const params = this.restService.buildHttpParamsObject({
      overrideDeleteDate: overrideDeleteDate,
    });
    return this.restService.remove$(this.BUILD_HTTP_ROUTE(`${contentId}`), {
      params,
    });
  }

  deleteVersion$(contentId: string, versionId: string): Observable<Object> {
    const params = {};
    return this.restService.remove$(
      this.BUILD_HTTP_ROUTE(`${contentId}/versions/${versionId}`),
      params
    );
  }

  spike$(
    contentId: string,
    reinstate: boolean = false
  ): Observable<ISpikeContentResponseDto> {
    const params = this.restService.buildHttpParamsObject({
      reinstate: reinstate,
    });
    return this.restService.put$<ISpikeContentResponseDto>(
      this.BUILD_HTTP_ROUTE(`${contentId}/spike`),
      { params }
    );
  }

  updateState$(
    versionId: string,
    status: string
  ): Observable<IContentStateChangeResponseDto> {
    return this.restService.put$<IContentStateChangeResponseDto>(
      this.BUILD_HTTP_ROUTE(`versions/${versionId}/state/${status}`),
      null
    );
  }

  repurpose$(
    versionId: string,
    initalState: string
  ): Observable<IRepurposeContentResponse> {
    const params = this.restService.buildHttpParamsObject({
      initialStateCode: initalState,
    });

    return this.restService.put$<IRepurposeContentResponse>(
      this.BUILD_HTTP_ROUTE(`versions/${versionId}/repurpose`),
      { params }
    );
  }
}
